import { useMutation, useQuery } from '@tanstack/react-query';

import { policies, type Policy, type PolicyInstallment } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatCivilDate } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { SkeletonText } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useToast } from '@/shared/ui/useToast';

import { descricaoDaParcela } from './apolice';
import {
  abrirBoleto,
  boletoEntregavel,
  MOTIVO_APOLICE_CANCELADA,
  MOTIVO_PARCELA_CANCELADA,
  MOTIVO_SEM_BOLETO,
  temBoletoNaApolice,
} from './boletoAnexado';

/**
 * Dialogo "Boletos" do OnPoint (APO-050 a APO-054, APO-177, APO-178): uma linha por
 * parcela, com a descricao, o vencimento, a situacao informada e o "Download do
 * boleto" da parcela.
 *
 * O OnPoint tem um botao so, abaixo da tabela, que abre todos os boletos. O navegador
 * abre uma janela por clique, e os outros boletos ficavam sem como baixar enquanto a
 * trilha dizia que tinham sido entregues. Por isso ha um botao por parcela, que pede
 * so o boleto dela (R5-M21), como no dialogo da cotacao.
 *
 * A descricao sai no formato brasileiro, e nao no americano do OnPoint (D25). Serve a
 * seguradora e a corretora, que ve e baixa o boleto das proprias apolices ("Visualizar
 * boleto(s)" da aba Apolices do tomador, TOM-174; P-BOL). As parcelas dizem se ha
 * boleto anexado; os enderecos temporarios so sao pedidos no clique, porque cada entrega
 * entra na trilha. Sem boleto anexado, o botao fica desligado com o motivo. O boleto
 * da parcela cancelada e os da apolice cancelada nao sao entregues (R5-M19).
 */
export function BilletsDialog({
  policy,
  open,
  onClose,
}: {
  policy: Policy | null;
  open: boolean;
  onClose: () => void;
}) {
  const { session } = useAuth();
  const toast = useToast();
  const daCorretora = session?.broker_scoped === true;
  const id = policy?.id ?? '';
  const parcelas = useQuery({
    queryKey: ['policy-installments', id],
    queryFn: ({ signal }) => policies.installments(id, signal),
    enabled: open && id !== '',
  });
  const baixar = useMutation({
    mutationFn: (parcela: PolicyInstallment) =>
      policies.billets(id, parcela.billet_document_id ?? undefined),
    onSuccess: (boletos) => {
      const boleto = boletos.billets[0];
      if (!boletos.available || !boleto) {
        toast.error(boletos.reason || MOTIVO_SEM_BOLETO);
        return;
      }
      abrirBoleto(boleto.url);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const modo = parcelas.data?.meta.billet_mode;
  const cancelada = policy?.status === 'cancelada';
  const disponivel = temBoletoNaApolice(modo, parcelas.data?.items, policy?.status);
  const motivo = cancelada ? MOTIVO_APOLICE_CANCELADA : MOTIVO_SEM_BOLETO;
  const boletoUnico = parcelas.data?.meta.single_billet === true;

  /** Por que a parcela nao oferece o boleto, ou null quando oferece. */
  function motivoDaParcela(parcela: PolicyInstallment): string | null {
    if (modo !== 'anexo') return MOTIVO_SEM_BOLETO;
    if (cancelada) return MOTIVO_APOLICE_CANCELADA;
    if (parcela.billet_document_id !== null && !boletoEntregavel(parcela)) {
      return MOTIVO_PARCELA_CANCELADA;
    }
    return parcela.billet_document_id === null ? MOTIVO_SEM_BOLETO : null;
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Boletos"
      description={
        policy ? `Apolice ${policy.external_number || policy.number}` : undefined
      }
      size="md"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      {parcelas.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar as parcelas">
          {parcelas.error.message}
        </Alert>
      ) : parcelas.isPending ? (
        <SkeletonText lines={3} />
      ) : parcelas.data.items.length === 0 ? (
        <p className="text-sm text-ink-600">
          Nenhuma parcela informada. A emissao copia as parcelas do financeiro da
          cotacao; sem elas, quem tem a alcada informa as parcelas no detalhe da
          apolice.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line text-left text-2xs font-semibold uppercase tracking-wide text-ink-500">
              <th scope="col" className="py-2">
                Descricao
              </th>
              <th scope="col" className="py-2">
                Vencimento
              </th>
              <th scope="col" className="py-2">
                Situacao
              </th>
              <th scope="col" className="py-2">
                Boleto
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {parcelas.data.items.map((parcela) => {
              const semBoleto = motivoDaParcela(parcela);
              return (
                <tr key={parcela.id}>
                  <td className="py-2 text-ink-800">
                    {descricaoDaParcela(
                      parcela.number,
                      parcela.amount,
                      parcela.interest,
                    )}
                  </td>
                  <td className="py-2 tabular-nums">
                    {formatCivilDate(parcela.due_on)}
                  </td>
                  <td className="py-2">
                    <StatusBadge kind="parcela" value={parcela.status} />
                  </td>
                  <td className="py-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={semBoleto !== null}
                      title={semBoleto ?? undefined}
                      loading={baixar.isPending && baixar.variables?.id === parcela.id}
                      aria-label={`Download do boleto da parcela ${parcela.number}`}
                      onClick={() => baixar.mutate(parcela)}
                    >
                      Download do boleto
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {parcelas.data && !disponivel ? (
        <p className="mt-4 text-xs text-ink-500">
          {motivo}
          {/* O modo de boleto e configuracao da empresa: so a seguradora le. */}
          {!daCorretora && !cancelada && modo !== 'anexo'
            ? ' A geracao de boleto esta desligada nesta empresa: a configuracao do boleto e a da apolice (Apolices > Configuracao).'
            : ''}
        </p>
      ) : null}
      {disponivel && boletoUnico ? (
        <p className="mt-4 text-xs text-ink-500">
          Boleto unico para todas as parcelas.
        </p>
      ) : null}
    </Modal>
  );
}
